import {
  createApi,
  BaseQueryFn,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
import baseAxiosInstance from "./api";
import {
  AccountDetailsPropTypes,
  ExtraUserPropTypes,
  UserType,
} from "../pages/Login/types";
// // Import the RTK Query methods from the React-specific entry point
// import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
//
// // Define our single API slice object
// export const apiSlice = createApi({
//   // The cache reducer expects to be added at `state.api` (already default - this is optional)
//   reducerPath: "api",
//   // All of our requests will have URLs starting with '/fakeApi'
//   baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_SURVEY_API_URL }),
//   // The "endpoints" represent operations and requests for this server
//   endpoints: (builder) => ({
//     getSurveys: builder.query({
//       query: () => "/surveys",
//     }),
//     getSurvey: builder.query({
//       query: (surveyId) => `/surveys/${surveyId}`,
//     }),
//     addNewSurvey: builder.mutation({
//       query: (initialSurvey) => ({
//         url: "/surveys",
//         method: "POST",
//         // Include the entire post object as the body of the request
//         body: initialSurvey,
//       }),
//     }),
//   }),
// });

// Export the auto-generated hook for the `getPosts` query endpoint
// export const { useGetSurveysQuery, useGetSurveyQuery } = apiSlice;

const axiosBaseQuery =
  (): // { baseUrl }: { baseUrl: string } = { baseUrl: "" }
  BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params }) => {
    try {
      // const result = await baseAxiosInstance({ url: baseUrl + url, method, data, params });
      const result = await baseAxiosInstance({
        url: url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const apiSlice = createApi({
  // baseQuery: axiosBaseQuery({
  //   baseUrl: process.env.REACT_APP_SURVEY_API_URL,
  // }),
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "Surveys",
    "Questions",
    "Sections",
    "Collectors",
    "ContactGroups",
    "Contacts",
    "Library",
  ],
  // prepareHeaders: (headers, { getState }) => {
  //   // By default, if we have a token in the store, let's use that for authenticated requests
  //   const token = (getState() as RootState).auth.token;
  //   if (token) {
  //     headers.set("authorization", `Bearer ${token}`);
  //   }
  //   return headers;
  // },
  endpoints: (builder) => ({
    // User
    getUser: builder.query({
      query: () => ({ url: "/user", method: "get" }),
      transformResponse: (response: { data: UserType }, meta, arg) =>
        response.data,
    }),
    resendEmailVerification: builder.mutation({
      query: (userDetail: { firstName: string; email: string }) => ({
        url: "/verify/resend",
        method: "POST",
        data: userDetail,
      }),
    }),
    sendPostVerificationEmail: builder.mutation({
      query: (userDetail: { email: string }) => ({
        url: "/verify/welcome",
        method: "POST",
        data: userDetail,
      }),
    }),
    sendUserResetPasswordEmail: builder.mutation({
      query: (userDetail: { email: string }) => ({
        url: "/reset",
        method: "POST",
        data: userDetail,
      }),
    }),
    confirmUserEmail: builder.mutation({
      query: (userDetail: { id: string }) => ({
        url: "/user/verify",
        method: "POST",
        data: userDetail,
      }),
    }),
    addExtraUserInformation: builder.mutation({
      query: (userDetail: ExtraUserPropTypes) => ({
        url: "/user/moreInfo",
        method: "POST",
        data: { ...userDetail, moreInfo: true, paymentPlan: "FREE" },
      }),
    }),
    updateAccountDetails: builder.mutation({
      query: (userDetail: AccountDetailsPropTypes) => ({
        url: "/user/account",
        method: "POST",
        data: userDetail,
      }),
    }),

    // Surveys
    getSurveys: builder.query({
      query: () => ({ url: "/surveys", method: "get" }),
      extraOptions: { maxRetries: 8 },
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(({ id }) => ({ type: "Surveys", id } as const)),
              { type: "Surveys", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Questions', id: 'LIST' }` is invalidated
            [{ type: "Surveys", id: "LIST" }],
    }),
    getSurvey: builder.query({
      query: (surveyId: string) => ({
        url: `/surveys/${surveyId}`,
        method: "GET",
      }),
      providesTags: (result, error, surveyId) => [
        { type: "Surveys", surveyId },
      ],
    }),
    addNewSurvey: builder.mutation({
      query: (surveyBody) => ({
        url: "/surveys",
        method: "POST",
        // Include the entire post object as the body of the request
        data: surveyBody,
      }),
      invalidatesTags: [{ type: "Surveys", id: "LIST" }],
    }),
    copySurvey: builder.mutation({
      query: (initialSurvey) => ({
        url: "/surveys/duplicate",
        method: "post",
        // Include the entire post object as the body of the request
        data: initialSurvey,
      }),
      invalidatesTags: [{ type: "Surveys", id: "LIST" }],
    }),
    copySurveyFromTemplate: builder.mutation({
      query: ({ surveyId, surveyDetail }) => ({
        url: `/surveys/${surveyId}/duplicate/template`,
        method: "post",
        // Include the entire post object as the body of the request
        data: surveyDetail,
      }),
      invalidatesTags: (result, error, { surveyId }) => [
        { type: "Surveys", surveyId },
        { type: "Sections", id: "LIST" },
        { type: "Questions", id: "LIST" },
      ],
    }),
    updateSurvey: builder.mutation({
      query: ({ surveyId, updatedSurvey }) => ({
        url: `/surveys/${surveyId}`,
        method: "put",
        // Include the entire post object as the body of the request
        data: updatedSurvey,
      }),
      invalidatesTags: (result, error, { surveyId }) => [
        { type: "Surveys", surveyId },
      ],
    }),
    updateSurveyLogo: builder.mutation({
      query: ({ surveyId, updatedSurvey }) => ({
        url: `/surveys/${surveyId}/upload`,
        method: "put",
        // Include the entire post object as the body of the request
        data: updatedSurvey,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    }),
    updateSurveyHeader: builder.mutation({
      query: ({ surveyId, updatedSurvey }) => ({
        url: `/surveys/${surveyId}/upload-header`,
        method: "put",
        // Include the entire post object as the body of the request
        data: updatedSurvey,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    }),

    // Sections
    getSections: builder.query({
      query: (surveyId: string) => ({
        url: `/surveys/${surveyId}/sections`,
        method: "get",
      }),
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(({ id }) => ({ type: "Sections", id } as const)),
              { type: "Sections", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Questions', id: 'LIST' }` is invalidated
            [{ type: "Sections", id: "LIST" }],
    }),
    addSection: builder.mutation({
      query: ({
        surveyId,
        sectionDetail,
      }: {
        surveyId: string;
        sectionDetail: any;
      }) => ({
        url: `/surveys/${surveyId}/sections`,
        method: "post",
        data: sectionDetail,
      }),
      invalidatesTags: (result, error, { surveyId }) => [
        { type: "Sections", id: "LIST" },
        { type: "Surveys", surveyId },
      ],
    }),
    updateSection: builder.mutation({
      query: ({
        surveyId,
        sectionId,
        sectionDetail,
      }: {
        surveyId: string;
        sectionId: string;
        sectionDetail: any;
      }) => ({
        url: `/surveys/${surveyId}/sections/${sectionId}`,
        method: "put",
        data: sectionDetail,
      }),
      invalidatesTags: (result, error, { sectionId }) => [
        { type: "Sections", sectionId },
      ],
    }),

    // Questions
    getQuestions: builder.query({
      query: (surveyId: string) => ({
        url: `/surveys/${surveyId}/questions`,
        method: "get",
      }),
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(({ id }) => ({ type: "Questions", id } as const)),
              { type: "Questions", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Questions', id: 'LIST' }` is invalidated
            [{ type: "Questions", id: "LIST" }],
    }),
    addQuestion: builder.mutation({
      query: ({
        surveyId,
        questionDetail,
      }: {
        surveyId: string;
        questionDetail: any;
      }) => ({
        url: `/surveys/${surveyId}/questions`,
        method: "post",
        data: questionDetail,
      }),
      invalidatesTags: (result, error, { surveyId }) => [
        { type: "Questions", id: "LIST" },
        { type: "Surveys", surveyId },
      ],
    }),

    updateQuestion: builder.mutation({
      query: ({
        surveyId,
        questionId,
        questionDetail,
      }: {
        surveyId: string;
        questionId: string;
        questionDetail: any;
      }) => ({
        url: `/surveys/${surveyId}/questions/${questionId}`,
        method: "put",
        data: questionDetail,
      }),
      invalidatesTags: (result, error, { questionId }) => [
        { type: "Questions", questionId },
      ],
    }),
    duplicateQuestion: builder.mutation({
      query: ({
        surveyId,
        questionId,
      }: {
        surveyId: string;
        questionId: string;
      }) => ({
        url: `/surveys/${surveyId}/questions/${questionId}/duplicate`,
        method: "post",
      }),
      invalidatesTags: (result, error, { surveyId }) => [
        { type: "Questions", id: "LIST" },
        { type: "Surveys", surveyId },
      ],
    }),
    deleteQuestion: builder.mutation({
      query: ({
        surveyId,
        questionId,
      }: {
        surveyId: string;
        questionId: string;
      }) => ({
        url: `/surveys/${surveyId}/questions/${questionId}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Post `id` only.
      invalidatesTags: (result, error, { questionId, surveyId }) => [
        { type: "Questions", questionId },
        { type: "Surveys", surveyId },
      ],
    }),

    // Responses
    getResponses: builder.query({
      query: (surveyId: string) => ({
        url: `/surveys/${surveyId}/responses`,
        method: "get",
      }),
      // configuration for an individual endpoint, overriding the api setting
      keepUnusedDataFor: 30,
    }),

    // Chart settings
    getChartSettings: builder.query({
      query: (surveyId: string) => ({
        url: `/surveys/${surveyId}/chart/settings`,
        method: "get",
      }),
    }),

    // Templates
    getTemplates: builder.query({
      query: () => ({ url: "/templates", method: "GET" }),
    }),
    getSingleTemplate: builder.query({
      query: (templateId: string) => ({
        url: `/templates/${templateId}/`,
        method: "GET",
      }),
    }),
    getTemplate: builder.mutation({
      query: (templateId: string) => ({
        url: `/templates/${templateId}`,
        method: "GET",
      }),
    }),

    // Collectors
    getSurveyCollectors: builder.query({
      query: ({ surveyId }) => ({
        url: `/collectors/survey/${surveyId}`,
        method: "GET",
      }),
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(({ id }) => ({ type: "Collectors", id } as const)),
              { type: "Collectors", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Questions', id: 'LIST' }` is invalidated
            [{ type: "Collectors", id: "LIST" }],
    }),
    addNewCollector: builder.mutation({
      query: (collectorBody) => ({
        url: "/collectors",
        method: "POST",
        // Include the entire post object as the body of the request
        data: collectorBody,
      }),
      invalidatesTags: [{ type: "Collectors", id: "LIST" }],
    }),
    updateCollector: builder.mutation({
      query: ({ collectorId, updatedCollector }) => ({
        url: `/collectors/${collectorId}`,
        method: "PUT",
        // Include the entire post object as the body of the request
        data: updatedCollector,
      }),
      invalidatesTags: (result, error, { collectorId }) => [
        { type: "Collectors", collectorId },
      ],
    }),
    deleteCollector: builder.mutation({
      query: ({ collectorId }: { collectorId: string }) => ({
        url: `/collectors/${collectorId}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Post `id` only.
      invalidatesTags: (result, error, { collectorId }) => [
        { type: "Collectors", id: "LIST" },
      ],
    }),
    sendEmailCollector: builder.mutation({
      query: ({ emailObj, collectorId }) => ({
        url: `/collectors/email/${collectorId}`,
        method: "POST",
        // Include the entire post object as the body of the request
        data: emailObj,
      }),
      invalidatesTags: (result, error, { collectorId }) => [
        { type: "Collectors", collectorId },
      ],
    }),

    // Contacts
    getContactGroup: builder.query({
      query: () => ({
        url: `/contacts/group/`,
        method: "GET",
      }),
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(
                ({ id }) => ({ type: "ContactGroups", id } as const)
              ),
              { type: "ContactGroups", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Questions', id: 'LIST' }` is invalidated
            [{ type: "ContactGroups", id: "LIST" }],
    }),
    getMyGroupContacts: builder.query({
      query: ({ groupId }) => ({
        url: `/contacts/${groupId}`,
        method: "GET",
      }),
      providesTags: () => [{ type: "Contacts", id: "LIST" }],
    }),
    getMyGroupContactsByMutating: builder.mutation({
      query: (groupId: string) => ({
        url: `/contacts/${groupId}`,
        method: "GET",
      }),
    }),
    addNewContactGroup: builder.mutation({
      query: (contactBody) => ({
        url: "/contacts/group",
        method: "POST",
        // Include the entire post object as the body of the request
        data: contactBody,
      }),
      invalidatesTags: [{ type: "ContactGroups", id: "LIST" }],
    }),
    updateMyContactGroup: builder.mutation({
      query: ({ groupId, updatedGroup }) => ({
        url: `/contacts/group/${groupId}`,
        method: "PUT",
        // Include the entire post object as the body of the request
        data: updatedGroup,
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ContactGroups", groupId },
      ],
    }),
    updateGroupContacts: builder.mutation({
      query: ({ groupId, updatedGroupContacts }) => ({
        url: `/contacts/group/list/${groupId}`,
        method: "PUT",
        // Include the entire post object as the body of the request
        data: updatedGroupContacts,
      }),
      invalidatesTags: [
        { type: "Contacts", id: "LIST" },
        { type: "ContactGroups", id: "LIST" },
      ],
    }),
    deleteContactGroup: builder.mutation({
      query: ({ groupId }: { groupId: string }) => ({
        url: `/contacts/group/${groupId}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Post `id` only.
      invalidatesTags: (result, error, { groupId }) => [
        { type: "ContactGroups", id: "LIST" },
      ],
    }),

    // Library
    getUserLibrary: builder.query({
      query: () => ({
        url: "/user/library",
        method: "GET",
      }),
      providesTags: (result) => [{ type: "Library", id: "LIST" }],
    }),
    addNewLogo: builder.mutation({
      query: (logoBody) => ({
        url: "/library/logo",
        method: "POST",
        // Include the entire post object as the body of the request
        data: logoBody,
      }),
      invalidatesTags: [{ type: "Library", id: "LIST" }],
    }),
    addNewColor: builder.mutation({
      query: (colorBody) => ({
        url: "/library/color",
        method: "POST",
        // Include the entire post object as the body of the request
        data: colorBody,
      }),
      invalidatesTags: [{ type: "Library", id: "LIST" }],
    }),
    deleteLogo: builder.mutation({
      query: ({ logoId }: { logoId: string }) => ({
        url: `/library/logo/${logoId}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Post `id` only.
      invalidatesTags: (result, error, { logoId }) => [
        { type: "Library", id: "LIST" },
      ],
    }),
    deleteColor: builder.mutation({
      query: ({ colorId }: { colorId: string }) => ({
        url: `/library/color/${colorId}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this Post `id` only.
      invalidatesTags: (result, error, { colorId }) => [
        { type: "Library", id: "LIST" },
      ],
    }),

    // WaitList
    getAllWaitList: builder.query({
      query: () => ({
        url: "/waitlist",
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetSurveysQuery,
  useGetSurveyQuery,
  useAddNewSurveyMutation,
  useUpdateSurveyMutation,
  useCopySurveyMutation,
  useCopySurveyFromTemplateMutation,
  useResendEmailVerificationMutation,
  useGetQuestionsQuery,
  useAddQuestionMutation,
  useDeleteQuestionMutation,
  useUpdateQuestionMutation,
  useDuplicateQuestionMutation,
  useGetResponsesQuery,
  useGetChartSettingsQuery,
  useGetTemplatesQuery,
  useGetTemplateMutation,
  useGetSingleTemplateQuery,
  useGetSectionsQuery,
  useAddSectionMutation,
  useUpdateSectionMutation,
  useUpdateCollectorMutation,
  useAddNewCollectorMutation,
  useDeleteCollectorMutation,
  useGetSurveyCollectorsQuery,
  useGetContactGroupQuery,
  useGetMyGroupContactsQuery,
  useAddNewContactGroupMutation,
  useUpdateMyContactGroupMutation,
  useUpdateGroupContactsMutation,
  useDeleteContactGroupMutation,
  useGetMyGroupContactsByMutatingMutation,
  useGetUserLibraryQuery,
  useAddNewLogoMutation,
  useAddNewColorMutation,
  useDeleteColorMutation,
  useDeleteLogoMutation,
  useSendEmailCollectorMutation,
  useGetAllWaitListQuery,
} = apiSlice;
